import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Trash2, Check, Sparkles } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { isPremium } from "@/components/PremiumGate";
import { Link } from "react-router-dom";
import { toast } from "sonner";

export default function Prayer() {
    const { user } = useAuth();
    const premium = isPremium(user);
    const [prayers, setPrayers] = useState([]);
    const [habits, setHabits] = useState([]);
    const [pText, setPText] = useState("");
    const [hName, setHName] = useState("");
    const [focus, setFocus] = useState("");
    const [genBusy, setGenBusy] = useState(false);
    const [companion, setCompanion] = useState(null);

    const loadAll = async () => {
        const [p, h] = await Promise.all([api.get("/prayer-list"), api.get("/habits")]);
        setPrayers(p.data);
        setHabits(h.data);
    };

    useEffect(() => { loadAll(); }, []);

    const addPrayer = async (e) => {
        e.preventDefault();
        if (!pText.trim()) return;
        await api.post("/prayer-list", { text: pText });
        setPText("");
        loadAll();
    };
    const togglePrayer = async (id) => { await api.patch(`/prayer-list/${id}`); loadAll(); };
    const delPrayer = async (id) => { await api.delete(`/prayer-list/${id}`); loadAll(); };

    const addHabit = async (e) => {
        e.preventDefault();
        if (!hName.trim()) return;
        await api.post("/habits", { name: hName });
        setHName("");
        loadAll();
    };
    const toggleHabit = async (id) => {
        const today = new Date().toISOString().slice(0, 10);
        await api.post(`/habits/${id}/toggle`, { date: today });
        loadAll();
    };
    const delHabit = async (id) => { await api.delete(`/habits/${id}`); loadAll(); };

    const generatePrayer = async () => {
        if (!premium) return;
        setGenBusy(true);
        try {
            const { data } = await api.post("/prayer/companion/generate", { focus: focus.trim() });
            setCompanion(data);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Couldn't compose a prayer right now.");
        } finally {
            setGenBusy(false);
        }
    };

    const today = new Date().toISOString().slice(0, 10);

    return (
        <div className="space-y-16">
            <header className="max-w-3xl">
                <div className="eyebrow-gold mb-4">Prayer &amp; rhythms</div>
                <h1 className="font-serif text-5xl md:text-7xl leading-[1.02]">Simple, faithful rhythms.</h1>
                <p className="mt-6 text-muted-foreground text-lg leading-relaxed">
                    No streaks. Only presence. Carry prayers, tend small rhythms, and — with Premium — ask for a
                    personalized prayer rooted in Scripture whenever you need one.
                </p>
            </header>

            {/* Prayer Companion (Premium) */}
            <section className="hairline rounded-lg p-8 md:p-10" data-testid="prayer-companion-section">
                <div className="eyebrow-gold mb-3 flex items-center gap-2">
                    <Sparkles className="w-3 h-3" strokeWidth={1.5} /> Prayer Companion
                </div>
                <div className="font-serif text-2xl md:text-3xl leading-snug max-w-2xl">
                    A prayer composed from your own stewardship — rooted in Scripture, not generic words.
                </div>
                {premium ? (
                    <>
                        <div className="mt-6 flex flex-col md:flex-row gap-3 max-w-2xl">
                            <Input
                                value={focus}
                                onChange={(e) => setFocus(e.target.value)}
                                placeholder="Optional: what you'd like the prayer to hold — e.g. a hard conversation this week"
                                className="h-11 rounded-md border-foreground/15 bg-transparent"
                                data-testid="prayer-focus-input"
                            />
                            <Button
                                onClick={generatePrayer}
                                disabled={genBusy}
                                data-testid="prayer-generate-btn"
                                className="bg-[hsl(var(--eden-forest))] text-[hsl(var(--eden-cream))] hover:bg-[hsl(var(--eden-forest))]/90 rounded-md h-11 px-6 text-xs uppercase tracking-[0.3em]"
                            >
                                {genBusy ? "Composing…" : "Compose a prayer"}
                            </Button>
                        </div>
                        {companion && (
                            <div className="mt-10 max-w-2xl" data-testid="prayer-companion-result">
                                <p className="font-serif text-xl md:text-2xl leading-[1.55] whitespace-pre-line">
                                    {companion.prayer}
                                </p>
                                {companion.scripture_reference && (
                                    <div className="mt-8 pt-6 border-t border-foreground/10">
                                        <div className="eyebrow-gold mb-2">{companion.scripture_reference} · KJV</div>
                                        <p className="text-sm italic text-muted-foreground leading-relaxed">
                                            &ldquo;{companion.scripture_verse}&rdquo;
                                        </p>
                                    </div>
                                )}
                            </div>
                        )}
                    </>
                ) : (
                    <p className="mt-4 text-sm text-muted-foreground max-w-xl leading-relaxed">
                        Premium composes personalized prayers from your current stewardship — your priority areas,
                        recent journal entries, and active prayers — each one rooted in a specific passage of Scripture.{" "}
                        <Link to="/membership" className="text-[hsl(var(--eden-gold))] hover:underline">Continue cultivating &rarr;</Link>
                    </p>
                )}
            </section>

            <div className="grid md:grid-cols-2 gap-12">
                {/* Prayer List */}
                <section>
                    <div className="eyebrow mb-4">Prayer List</div>
                    <form onSubmit={addPrayer} className="flex gap-2 mb-6" data-testid="prayer-form">
                        <Input
                            value={pText}
                            onChange={(e) => setPText(e.target.value)}
                            placeholder="Someone or something to pray over…"
                            className="h-11 rounded-md border-foreground/15 bg-transparent"
                            data-testid="prayer-input"
                        />
                        <Button type="submit" className="bg-[hsl(var(--eden-forest))] text-[hsl(var(--eden-cream))] hover:bg-[hsl(var(--eden-forest))]/90 rounded-md" data-testid="prayer-add-btn">Add</Button>
                    </form>
                    <ul className="space-y-2">
                        {prayers.length === 0 && <li className="text-sm text-muted-foreground">No prayers yet.</li>}
                        {prayers.map((p) => (
                            <li key={p.item_id} className="hairline rounded-md p-4 flex items-center gap-3 group">
                                <button
                                    onClick={() => togglePrayer(p.item_id)}
                                    className={`w-5 h-5 rounded-sm border flex items-center justify-center transition-colors ${
                                        p.answered ? "bg-[hsl(var(--eden-gold))] border-[hsl(var(--eden-gold))]" : "border-foreground/30"
                                    }`}
                                    aria-label="Mark as answered"
                                >
                                    {p.answered && <Check className="w-3 h-3 text-[hsl(var(--eden-forest))]" strokeWidth={2} />}
                                </button>
                                <span className={`flex-1 text-sm ${p.answered ? "line-through text-muted-foreground" : ""}`}>{p.text}</span>
                                <button onClick={() => delPrayer(p.item_id)} className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive" aria-label="Delete">
                                    <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                                </button>
                            </li>
                        ))}
                    </ul>
                </section>

                {/* Habits */}
                <section>
                    <div className="eyebrow mb-4">Weekly Habits</div>
                    <form onSubmit={addHabit} className="flex gap-2 mb-6" data-testid="habit-form">
                        <Input
                            value={hName}
                            onChange={(e) => setHName(e.target.value)}
                            placeholder="A small rhythm to cultivate…"
                            className="h-11 rounded-md border-foreground/15 bg-transparent"
                            data-testid="habit-input"
                        />
                        <Button type="submit" className="bg-[hsl(var(--eden-forest))] text-[hsl(var(--eden-cream))] hover:bg-[hsl(var(--eden-forest))]/90 rounded-md" data-testid="habit-add-btn">Add</Button>
                    </form>
                    <ul className="space-y-2">
                        {habits.length === 0 && <li className="text-sm text-muted-foreground">No habits yet.</li>}
                        {habits.map((h) => {
                            const done = h.completions.includes(today);
                            return (
                                <li key={h.habit_id} className="hairline rounded-md p-4 flex items-center gap-3 group">
                                    <button
                                        onClick={() => toggleHabit(h.habit_id)}
                                        className={`w-5 h-5 rounded-sm border flex items-center justify-center transition-colors ${
                                            done ? "bg-[hsl(var(--eden-gold))] border-[hsl(var(--eden-gold))]" : "border-foreground/30"
                                        }`}
                                        aria-label="Mark tended today"
                                    >
                                        {done && <Check className="w-3 h-3 text-[hsl(var(--eden-forest))]" strokeWidth={2} />}
                                    </button>
                                    <span className="flex-1 text-sm">{h.name}</span>
                                    <span className="text-xs text-muted-foreground">{h.completions.length} days tended</span>
                                    <button onClick={() => delHabit(h.habit_id)} className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive" aria-label="Delete">
                                        <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                </section>
            </div>
        </div>
    );
}
